import { Injectable } from '@angular/core';
import {
  CSSActiveClass,
  CSSHighlightedClass,
  CSSNewClass,
  CSSResizedClass,
} from '../../../util/calendar-style.constants';
import { CalendarService } from '../../../services/calendar.service';
import { CalendarScrollbarService } from '../../../services/calendar-scrollbar.service';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { CssAttributes } from '../rendering/css-attributes';
import { TimeBlockDayOrWeekFixIntervalType } from '../../../../../core/models/timeblock/time-block-view-type.model';
import { TimeBlockStructureService } from '../time-block-structure/time-block-structure.service';
import { TimeBlockElementSelectorService } from '../rendering/time-block-element-selector.service';
import { CalendarViews } from '../../../calendar-types';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';

@Injectable()
export class TimeBlockHighlightingService {
  constructor(
    private readonly calendarService: CalendarService,
    private readonly timeBlockStructureService: TimeBlockStructureService,
    private readonly calendarScrollbarService: CalendarScrollbarService,
    private readonly shiveRenderService: ShiveRenderService,
  ) {}

  /**
   * Create a visual copy of the clicked time block to highlight it in the frontend.
   */
  public createHighlightedTimeBlockClone(timeBlock: ITimeBlockComponentItem): void {
    let parts: ITimeBlockComponentItem[];
    const calendarView = this.calendarService.model.calendarViewMode;
    if (
      (calendarView === CalendarViews.DayGrid || calendarView === CalendarViews.WeekGrid) &&
      timeBlock.timeBlockModel.fixInterval !== null
    ) {
      const headPart = this.timeBlockStructureService.retrieveTimeBlockPart(
        timeBlock.id,
        timeBlock.timeBlockModel.type,
        0,
      ) as ITimeBlockComponentItem;
      parts = [headPart];
    } else {
      parts = this.timeBlockStructureService.retrieveTimeBlockPart(
        timeBlock.id,
        timeBlock.timeBlockModel.type,
        -1,
      ) as ITimeBlockComponentItem[];
    }

    parts.forEach((timeBlockPart) => {
      if (timeBlockPart.timeBlockModel.componentRef === null) {
        return;
      }

      const htmlWrapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(timeBlockPart);
      const clone = htmlWrapper.cloneNode(true) as HTMLElement;
      if (timeBlock.timeBlockModel.timeBlockViewType instanceof TimeBlockDayOrWeekFixIntervalType) {
        // Horizontal block
        this.calcHorizontalHighlightedBlockGeometry(clone, htmlWrapper);
      } else {
        this.calcVerticalHighlightedBlockGeometry(clone, htmlWrapper, timeBlock);
      }

      this.shiveRenderService.removeStyle(clone, CssAttributes.transform);
      this.shiveRenderService.removeStyle(clone, CssAttributes.zIndex);
      this.shiveRenderService.addClass(clone, CSSHighlightedClass);
      this.shiveRenderService.addClass(clone, 'dialog-shadow');

      this.shiveRenderService.removeClass(clone, CSSActiveClass);
      this.shiveRenderService.removeClass(clone, CSSNewClass);
      this.shiveRenderService.removeClass(clone, CSSResizedClass);
      this.shiveRenderService.insertBefore(
        document.querySelector('.time-entry-dialog-wrapper'),
        clone,
        document.querySelector('.k-dialog'),
      );
    });
  }

  /**
   * Set geometrical and positional data of the horizontal highlighted block.
   */
  private calcHorizontalHighlightedBlockGeometry(
    clone: HTMLElement,
    timeBlockEl: HTMLElement,
  ): void {
    clone.style.top = `${timeBlockEl.getBoundingClientRect().top}px`;
    clone.style.left = `${timeBlockEl.getBoundingClientRect().left}px`;

    const computedStyle = getComputedStyle(timeBlockEl);
    clone.style.width = `${timeBlockEl.getBoundingClientRect().width - parseFloat(computedStyle.paddingRight)}px`;
    clone.style.height = `${timeBlockEl.getBoundingClientRect().height}px`;
  }

  /**
   * Set geometrical and positional data of the vertical highlighted block.
   */
  private calcVerticalHighlightedBlockGeometry(
    clone: HTMLElement,
    timeBlockEl: HTMLElement,
    timeBlock: ITimeBlockComponentItem,
  ): void {
    const geometryData = timeBlock.timeBlockModel.timeBlockViewType.geometryData;
    const calendarTopEdgeToBlockBottomEdge = geometryData.calendarTopEdgeToTimeBlockBottomEdge;

    const calendarBodyOffsetTop = this.calendarService.model.geometryData.calendarBodyOffsetTop;
    const calendarBodyHeight = this.calendarService.model.geometryData.calendarBodyVisibleHeight;
    const timeBlockTop = timeBlockEl.getBoundingClientRect().top;
    const timeBlockLeft = timeBlockEl.getBoundingClientRect().left;
    const timeBlockHeight = timeBlockEl.offsetHeight;

    if (timeBlockTop < calendarBodyOffsetTop) {
      clone.style.top = `${calendarBodyOffsetTop}px`;
      clone.style.height = `${timeBlockHeight - calendarBodyOffsetTop + timeBlockTop}px`;
    } else if (timeBlockTop + timeBlockHeight > calendarBodyOffsetTop + calendarBodyHeight) {
      clone.style.top = `${timeBlockTop}px`;
      clone.style.height = `${timeBlockHeight - calendarTopEdgeToBlockBottomEdge + this.calendarScrollbarService.calendarBodyScrollbar.scrollData.scrollPosY + calendarBodyHeight}px`;
    } else {
      clone.style.top = `${timeBlockTop}px`;
      clone.style.height = `${timeBlockHeight}px`;
    }

    clone.style.width = `${timeBlockEl.offsetWidth}px`;
    clone.style.left = `${timeBlockLeft}px`;
  }
}
